import { clearUndefinedKeys } from '../utils/utils';
import { AccountService } from './AccountService';

interface GTMPayload {
  event: GTMEventType;
  payload: Record<string, any>;
}

export interface WindowWithGTMLayer {
  dataLayer: { push: (event: GTMPayload) => void };
}

export enum GTMEventType {
  PageChange = 'PageChange',
  MenuCategoryChange = 'MenuCategoryChange',
  OrderHistoryRefundsFilterChange = 'OrderHistoryRefundsFilterChange',

  SignupLeadCreated = 'SignupLeadCreated',
  SignupLeadUpdated = 'SignupLeadUpdated',
  SignupLeadSubmitted = 'SignupLeadSubmitted',
}

interface GTMEvent {
  type: GTMEventType;
  payload: Record<string, any>;
}

function getDataLayer() {
  return (window as unknown as WindowWithGTMLayer).dataLayer;
}

export class TrackingService {
  constructor(private readonly accountService: AccountService) {}

  private pushEvent(eventData: GTMPayload, delayEvent: boolean = false) {
    const layer = getDataLayer();
    if (!layer) {
      return;
    }

    const cleanEventData = {
      ...eventData,
      ...clearUndefinedKeys(eventData.payload),
    };

    // Timeout is required in order for the generated event to include
    // the updated document title, as the title update is hooked to
    // location.pathname updates (just like this event creation), and
    // cannot be easily prioritised before this event
    if (!delayEvent) {
      layer.push(cleanEventData);
    } else {
      setTimeout(() => layer.push(cleanEventData), 10);
    }
  }

  private pushUserEvent({ type, payload }: GTMEvent, delayEvent: boolean = false) {
    const accountData = this.accountService.getSelectedProviderData$().state;
    const userDetails = this.accountService.getAccountDetails$().state;

    this.pushEvent(
      {
        event: type,
        payload: {
          providerName: accountData?.providerName,
          vendorName: accountData?.vendorName,
          countryCode: accountData?.countryCode,
          cityId: accountData?.cityId,
          providerId: accountData?.providerId,
          userEmail: userDetails.account_data.email,
          ...payload,
        },
      },
      delayEvent,
    );
  }

  private pushPublicEvent({ type, payload }: GTMEvent) {
    this.pushEvent({
      event: type,
      payload,
    });
  }

  public pushPageChangeEvent(payload: PageChangePayload) {
    return this.pushUserEvent({ type: GTMEventType.PageChange, payload }, true);
  }

  public pushMenuCategoryChangeEvent(payload: MenuCategoryChangePayload) {
    return this.pushUserEvent({ type: GTMEventType.MenuCategoryChange, payload });
  }

  public pushOrderHistoryRefundsFilterChangeEvent(payload: OrderHistoryRefundsFilterChangePayload) {
    return this.pushUserEvent({ type: GTMEventType.OrderHistoryRefundsFilterChange, payload });
  }

  public pushSignupLeadCreatedEvent(payload: SignupSubmissionPayload) {
    return this.pushPublicEvent({ type: GTMEventType.SignupLeadCreated, payload });
  }

  public pushSignupLeadUpdatedEvent(payload: SignupSubmissionPayload) {
    return this.pushPublicEvent({ type: GTMEventType.SignupLeadUpdated, payload });
  }

  public pushSignupLeadSubmittedEvent(payload: SignupSubmissionPayload) {
    return this.pushPublicEvent({ type: GTMEventType.SignupLeadSubmitted, payload });
  }
}

export interface PageChangePayload {
  url: string;
}

export interface MenuCategoryChangePayload {
  id: number;
  name: string;
}

export interface OrderHistoryRefundsFilterChangePayload {
  value: boolean;
}

export interface SignupSubmissionPayload {
  continue_token: string;
  stepId: string;
  language?: string;
  country?: string;
  city?: string;
  sha256_email_address?: string;
  sha256_phone_number?: string;
  sha256_first_name?: string;
  sha256_last_name?: string;
}
